import { SetMirrorData } from '@type/job-add';
import {
  Label, ComboBox, Input,
  RecordInterface, SetFormValues,
} from '@components/common/Forms';
import { useTranslation } from 'next-i18next';

/** Props */
interface JobEmploymentTypesProps {
  apiEmploymentTypes?: ApiSchema<'OptionItem'>[],
  record: RecordInterface,
  formValues: object,
  setFormValues: SetFormValues,
  initialSelectedValues?: string[],
  checkboxName?: string,
  labelDescriptionClass?: string,
  setMirrorData?: SetMirrorData,
}

/**
 * @description Job Employment Types component. To be used in a form. The text input field is omitted from submit.
 * - `apiEmploymentTypes` - static list of employment types from the API
 * - `record` - record function from useBjForm
 * - `formValues` - form values object from useBjForm
 * - `setFormValues` - setFormValues function from useBjForm
 * - `initialSelectedValues` - initial selected values for combobox (ie: useBjForm.withDefaultValues.careerLevels)
 * - `checkboxName` - name of the checkbox; default is 'employmentTypes'
 * - `labelDescriptionClass` - additional classes for the label description
 * - `setMirrorData` - function to update the mirror form state
 */
export const JobEmploymentTypes = (props: JobEmploymentTypesProps) => {
  // Destructure props
  const {
    apiEmploymentTypes,
    record,
    formValues,
    setFormValues,
    initialSelectedValues,
    checkboxName = 'employmentTypes',
    labelDescriptionClass = '',
    setMirrorData,
  } = props;

  // Translation
  const { t } = useTranslation();

  // Render
  // **************************
  return (
    <Label
      as="div"
      description={t('job.add.label.employment_type')}
      descriptionClass={labelDescriptionClass}
    >
      <ComboBox
        className="max-w-full text-ellipsis pr-18"
        options={{
          staticList: apiEmploymentTypes,
          valueFromKey: 'name',
          translateFn: t,
          unmount: false,
          keepOpen: true,
          behaveAs: 'select',
          selectOptions: {
            multiple: true,
            checkboxName,
            checkboxValueFromKey: 'id',
            setFormValues,
            initialSelectedValues,
            updatedSelectedValues: formValues?.[checkboxName as keyof typeof formValues],
          },
        }}
        onBlur={() => setMirrorData?.()}
        onClear={() => {
          setFormValues({ [checkboxName]: [] });
          setMirrorData?.();
        }}
        {...record('employmentTypesSelector', {
          removeFromSubmit: true,
        })}
        renderList={(
          item,
          index,
          listKey,
          getItemProps,
          listStyling,
        ) => {
          const employmentItem = item as ApiSchema<'OptionItem'>;
          return (
            <li key={listKey} {...getItemProps({ item, index })} className="px-3">
              <span className={`${listStyling} cursor-pointer items-center text-sm`}>
                <Input
                  type="checkbox"
                  {...record(checkboxName, {
                    id: `${checkboxName}${employmentItem.id}`,
                    defaultValue: employmentItem.id,
                    valueAsArray: true,
                  })}
                />
                <span className="pl-2.5">
                  {employmentItem.name}
                </span>
              </span>
            </li>
          );
        }}
      />
    </Label>
  );
};
